import type { Annotation } from "@rowan/prisma";
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trpc } from "../../../utils/trpc";
import { useQuery } from "../../hooks/useQuery";
import { PublicationContext } from "../WithPublication/PublicationContext";
import { AnnotationContext } from "./AnnotationContext";

export const AnnotationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const didTapMenuRef = useRef(false);
  const hasHadAnnotationsRef = useRef(false);
  const query = useQuery();
  const annotationId = query.get("annotationId");
  const { setScrollDisabled, publication } = useContext(PublicationContext);
  const publicationId = publication?.id;
  const [annotationsOpen, setAnnotationsOpen] = useState(true);
  const [focusedId, setFocusedId] = useState<string | undefined>(
    annotationId || undefined,
  );

  const { data: annotations, isLoading } = trpc.getAnnotations.useQuery(
    { publicationId: publicationId },
    {
      enabled: !!publicationId,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      cacheTime: 0,
    },
  );

  const updateFocusedId = useCallback((id: string | undefined) => {
    setFocusedId(id);
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);

    if (id) {
      params.set("annotationId", id);
    } else {
      params.delete("annotationId");
    }

    const newUrl = `${pathname}?${params.toString()}${hash}`;
    window.history.replaceState(null, "", newUrl);
  }, []);

  useEffect(() => {
    hasHadAnnotationsRef.current = false;
    setAnnotationsOpen(true);
  }, [publicationId]);

  useLayoutEffect(() => {
    if (annotations && annotations.length > 0) {
      hasHadAnnotationsRef.current = true;
    }
    if (!isLoading && annotations?.length === 0) {
      if (hasHadAnnotationsRef.current) {
        const scrollContainer = document.getElementById("ereader-content");
        const containerRect = scrollContainer?.getBoundingClientRect();
        const foundEl = containerRect
          ? (document.elementFromPoint(
              containerRect.left + containerRect.width / 2,
              containerRect.top + 1,
            ) as HTMLElement | null)
          : null;
        const referenceEl =
          foundEl && scrollContainer?.contains(foundEl) ? foundEl : null;
        // referenceTopBefore records the position of the element at the top of your screen as you delete the last annotation
        const referenceTopBefore = referenceEl?.getBoundingClientRect().top;
        setAnnotationsOpen(false);
        setScrollDisabled(true);
        requestAnimationFrame(() => {
          if (
            scrollContainer &&
            referenceEl &&
            referenceTopBefore !== undefined
          ) {
            // referenceTopAfter records the position of the same element after you delete the last annotation
            const referenceTopAfter = referenceEl.getBoundingClientRect().top;
            // this repositions the scroll to account for the change in layout caused by the annotations disappearing
            // so you don't lose your place on the page
            scrollContainer.scrollTop += referenceTopAfter - referenceTopBefore;
          }
          setScrollDisabled(false);
        });
      } else {
        setAnnotationsOpen(false);
      }
    }
  }, [annotations, isLoading, setScrollDisabled]);

  const context = useMemo(() => {
    return {
      annotations: annotations?.map((annotation) => ({
        ...annotation,
        lastModified: new Date(annotation.lastModified),
        createdAt: new Date(annotation.createdAt),
      })) as Annotation[],
      annotationsOpen,
      setAnnotationsOpen,
      focusedId: focusedId,
      setFocusedId: updateFocusedId,
      didTapMenuRef,
    };
  }, [
    annotations,
    annotationsOpen,
    setAnnotationsOpen,
    focusedId,
    updateFocusedId,
    didTapMenuRef,
  ]);

  return (
    <AnnotationContext.Provider value={context}>
      {children}
    </AnnotationContext.Provider>
  );
};
